// action-capture.ts — Parseur pur de la barre de capture d'action (piste 0.A).
//
// Logique DÉTERMINISTE extraite de la glu DOM (component-action-capture) : à
// partir du texte libre saisi, isole le TITRE et résout la DATE d'échéance et
// la PRIORITÉ. Les mentions `@personne` et rattachements `#entité` sont gérés
// par le composant (ils demandent une résolution contre les référentiels +
// désambiguïsation homonymes), pas ici : ce module ne voit que ce qui est
// résoluble sans données (dates FR + priorité `!`).
//
// Convention : `today` est passé explicitement (ISO `YYYY-MM-DD`) pour rester
// pur et testable — le composant fournit `todayStr()`. Aucun `new Date()` sans
// argument ici.
//
// Cf. docs/UX_BARRE_CAPTURE_ACTION.md §7 (parseur de dates) pour les règles et
// le tableau de cas de test (référence sam. 4 juil. 2026).

export interface CaptureParse {
  // Titre = résidu après retrait des spans date + priorité, espaces normalisés.
  title: string;
  // Priorité 0-4 (0 = très haute) ou null si aucun `!`. `!`→2, `!!`→1, `!!!`→0.
  priority: number | null;
  // Échéance ISO `YYYY-MM-DD`, ou null si aucune date reconnue.
  deadline: string | null;
  // `deadline` est antérieure à `today` (action en retard — accepté, warning).
  deadlinePast: boolean;
  // Une 2e date a été détectée et ignorée (la date révisée se saisit ailleurs).
  secondDateIgnored: boolean;
  // Motif date-like invalide (ex. `07/13`, mois 13) : refus + hint « préciser ».
  invalidDate: boolean;
  // Motif date-like ambigu (ex. `mi-juillet`, `T3`) : refus + hint « préciser ».
  ambiguousDate: boolean;
}

export type CaptureRole = 'R' | 'A' | 'C' | 'I';

export interface CapturePersonInput {
  id: number;
  role: CaptureRole;
}
export interface CaptureAttachmentInput {
  kind: 'project' | 'committee' | 'structure';
  id: number;
}
export interface CapturePayloadInput {
  parse: CaptureParse;
  people: ReadonlyArray<CapturePersonInput>;
  ownerId: number | null;
  attachments: ReadonlyArray<CaptureAttachmentInput>;
  defaultStatusId: number | null;
}

interface DateParts {
  y: number;
  m: number; // 1-12
  d: number; // 1-31
}

interface DateSpan {
  index: number;
  length: number;
  iso: string;
}

// ── Helpers date purs (UTC, indépendants du fuseau) ─────────────────────────

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function isoFromParts(p: DateParts): string {
  return `${p.y}-${pad2(p.m)}-${pad2(p.d)}`;
}

function daysInMonth(y: number, m: number): number {
  // m = 1-12. `new Date(Date.UTC(y, m, 0))` = dernier jour du mois m.
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

function parseToday(today: string): DateParts {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(today);
  if (!m) {
    // Fallback défensif : ne devrait pas arriver (todayStr() est ISO strict).
    return { y: 2026, m: 1, d: 1 };
  }
  return { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) };
}

function addDays(today: DateParts, days: number): string {
  const base = Date.UTC(today.y, today.m - 1, today.d);
  const dt = new Date(base + days * 86400000);
  return `${dt.getUTCFullYear()}-${pad2(dt.getUTCMonth() + 1)}-${pad2(dt.getUTCDate())}`;
}

function addMonths(today: DateParts, months: number): string {
  const totalM = today.m - 1 + months;
  const y = today.y + Math.floor(totalM / 12);
  const m = ((totalM % 12) + 12) % 12; // 0-11
  const d = Math.min(today.d, daysInMonth(y, m + 1));
  return isoFromParts({ y, m: m + 1, d });
}

function weekdayUtc(today: DateParts): number {
  // 0 = dimanche … 6 = samedi (getUTCDay).
  return new Date(Date.UTC(today.y, today.m - 1, today.d)).getUTCDay();
}

// ── Normalisation pour matching (même longueur → indices alignés) ───────────

// Remplace 1 caractère accentué par 1 caractère base (préserve les indices, à
// la différence de NFD qui décompose). Puis minuscule (même longueur).
function normalizeForMatch(s: string): string {
  return s
    .replace(/[àâä]/g, 'a')
    .replace(/[éèêë]/g, 'e')
    .replace(/[îï]/g, 'i')
    .replace(/[ôö]/g, 'o')
    .replace(/[ùûü]/g, 'u')
    .replace(/[ç]/g, 'c')
    .replace(/[ÀÂÄ]/g, 'A')
    .replace(/[ÉÈÊË]/g, 'E')
    .toLowerCase();
}

const WEEKDAYS: Record<string, number> = {
  dimanche: 0,
  lundi: 1,
  mardi: 2,
  mercredi: 3,
  jeudi: 4,
  vendredi: 5,
  samedi: 6,
};

// ── Extraction des dates ────────────────────────────────────────────────────

// Renvoie tous les spans date résolus (numériques + relatifs), plus les
// signaux invalidDate / ambiguousDate rencontrés.
function collectDateSpans(
  norm: string,
  today: DateParts,
): { spans: DateSpan[]; invalid: boolean; ambiguous: boolean } {
  const spans: DateSpan[] = [];
  let invalid = false;

  // 1) Numérique JJ/MM[/AAAA] avec séparateur / . -
  // eslint-disable-next-line security/detect-unsafe-regex -- quantificateurs bornés (\d{1,2}, \d{2,4}), aucun nesting → linéaire.
  const numRe = /(?<![\d/.-])(\d{1,2})([/.-])(\d{1,2})(?:\2(\d{2,4}))?(?![\d/.-])/g;
  for (let m = numRe.exec(norm); m; m = numRe.exec(norm)) {
    const day = Number(m[1]);
    const sep = m[2];
    const mon = Number(m[3]);
    const yearRaw = m[4];
    const slashDash = sep === '/' || sep === '-';
    if (mon >= 1 && mon <= 12 && day >= 1 && day <= daysInMonth(today.y, mon)) {
      const y = yearRaw
        ? yearRaw.length <= 2
          ? 2000 + Number(yearRaw)
          : Number(yearRaw)
        : today.y;
      // Recalcule la validité du jour avec l'année réelle (années bissextiles).
      if (day <= daysInMonth(y, mon)) {
        spans.push({
          index: m.index,
          length: m[0].length,
          iso: isoFromParts({ y, m: mon, d: day }),
        });
      }
    } else if (slashDash && (mon > 12 || day > 31)) {
      // Motif clairement daté mais invalide (ex. 07/13) → refus explicite.
      invalid = true;
    }
    // Sinon (ex. "2.0") : ni date ni invalide, on ignore silencieusement.
  }

  // 2) Relatifs — ordre de priorité, on écarte les chevauchements.
  const addRel = (index: number, length: number, iso: string): void => {
    if (index < 0) return;
    if (spans.some((s) => index < s.index + s.length && index + length > s.index)) return;
    spans.push({ index, length, iso });
  };

  const matchOne = (re: RegExp, resolve: (m: RegExpExecArray) => string | null): void => {
    const mm = re.exec(norm);
    if (!mm) return;
    const iso = resolve(mm);
    if (iso) addRel(mm.index, mm[0].length, iso);
  };

  matchOne(/aujourd['’]hui/, () => addDays(today, 0));
  matchOne(/\bapres-demain\b/, () => addDays(today, 2));
  matchOne(/\bdemain\b/, () => addDays(today, 1));
  matchOne(/\b(lundi|mardi|mercredi|jeudi|vendredi|samedi|dimanche)\b/, (m) => {
    const target = WEEKDAYS[m[1] as string] as number;
    const dow = weekdayUtc(today);
    const delta = (target - dow + 7) % 7 || 7; // occurrence future stricte
    return addDays(today, delta);
  });
  matchOne(/\bdans\s+(\d{1,3})\s+(jours?|semaines?|mois)\b/, (m) => resolveDelta(today, m));
  matchOne(/\bd['’]ici\s+(\d{1,3})\s+(jours?|semaines?|mois)\b/, (m) => resolveDelta(today, m));
  matchOne(/\bfin\s+de\s+(semaine|mois|trimestre)\b/, (m) => resolveEndOf(today, m[1] as string));

  // 3) Marqueurs ambigus (refus + hint) — seulement s'ils n'ont produit aucune
  // date exploitable au même endroit.
  const ambRe =
    /\b(mi-[a-z]+|debut\s+[a-z]+|courant\s+[a-z]+|la\s+semaine\s+prochaine|bientot|asap|prochainement|[tq][1-4])\b/;
  const ambiguous = ambRe.test(norm);

  return { spans, invalid, ambiguous };
}

function resolveDelta(today: DateParts, m: RegExpExecArray): string {
  const n = Number(m[1]);
  const unit = m[2] as string;
  if (unit.startsWith('jour')) return addDays(today, n);
  if (unit.startsWith('semaine')) return addDays(today, n * 7);
  return addMonths(today, n);
}

function resolveEndOf(today: DateParts, kind: string): string {
  if (kind === 'semaine') {
    const dow = weekdayUtc(today);
    const delta = (5 - dow + 7) % 7; // vendredi à venir (aujourd'hui inclus)
    return addDays(today, delta);
  }
  if (kind === 'mois') {
    return isoFromParts({ y: today.y, m: today.m, d: daysInMonth(today.y, today.m) });
  }
  // Trimestre civil : dernier jour du trimestre courant.
  const endMonth = Math.ceil(today.m / 3) * 3; // 3, 6, 9 ou 12
  return isoFromParts({ y: today.y, m: endMonth, d: daysInMonth(today.y, endMonth) });
}

// ── Extraction de la priorité ───────────────────────────────────────────────

interface PrioritySpan {
  index: number;
  length: number;
  value: number;
}

function collectPrioritySpans(text: string): PrioritySpan[] {
  const out: PrioritySpan[] = [];
  // `!`, `!!` ou `!!!` en token isolé (bordé par début/espace ↔ fin/espace).
  const re = /(?:^|\s)(!{1,3})(?=\s|$)/g;
  for (let m = re.exec(text); m; m = re.exec(text)) {
    const bangs = m[1] as string;
    const start = m.index + m[0].indexOf('!');
    out.push({ index: start, length: bangs.length, value: 3 - bangs.length });
  }
  return out;
}

// ── Retrait de spans + normalisation d'espaces ──────────────────────────────

function stripSpans(text: string, spans: Array<{ index: number; length: number }>): string {
  if (spans.length === 0) return text;
  const sorted = [...spans].sort((a, b) => a.index - b.index);
  let out = '';
  let cursor = 0;
  for (const s of sorted) {
    if (s.index < cursor) continue; // sécurité anti-chevauchement
    out += text.slice(cursor, s.index);
    cursor = s.index + s.length;
  }
  out += text.slice(cursor);
  return out;
}

// Retire la traîne de mots-déclencheurs (« avant le », « pour le », « d'ici »…)
// restée accolée à gauche de la date retirée, pour ne pas laisser le titre finir
// par « … avant le ». Consomme une SUITE de déclencheurs consécutifs.
const TRIGGER_WORDS = new Set([
  'avant',
  'apres',
  'pour',
  "d'ici",
  'dici',
  'des',
  'jusqu',
  "jusqu'au",
  'jusquau',
  'le',
  'la',
]);

function stripTrailingTriggers(text: string): string {
  const trimmed = text.trimEnd();
  if (!trimmed) return trimmed;
  const parts = trimmed.split(/\s+/);
  const isTrigger = (w: string): boolean =>
    TRIGGER_WORDS.has(normalizeForMatch(w).replace(/[’]/g, "'"));
  while (parts.length > 0 && isTrigger(parts[parts.length - 1] as string)) parts.pop();
  return parts.join(' ');
}

function collapse(text: string): string {
  return text.replace(/\s{2,}/g, ' ').trim();
}

// ── Fonction principale ─────────────────────────────────────────────────────

export function parseCaptureText(input: string, today: string): CaptureParse {
  const text = input ?? '';
  const todayParts = parseToday(today);
  const norm = normalizeForMatch(text);

  const { spans: dateSpans, invalid, ambiguous } = collectDateSpans(norm, todayParts);
  dateSpans.sort((a, b) => a.index - b.index);

  const chosen = dateSpans[0] ?? null;
  const deadline = chosen ? chosen.iso : null;
  const secondDateIgnored = dateSpans.length > 1;
  const deadlinePast = deadline !== null && deadline < today;
  // L'ambiguïté n'est signalée que si aucune date exploitable n'a été trouvée.
  const ambiguousDate = ambiguous && deadline === null;

  const prioritySpans = collectPrioritySpans(text);
  const priority = prioritySpans.length
    ? Math.min(...prioritySpans.map((p) => p.value)) // plus de « ! » = plus prioritaire
    : null;

  // Titre : retire la date choisie + tous les tokens priorité, puis nettoie un
  // éventuel déclencheur orphelin et normalise les espaces.
  const removal: Array<{ index: number; length: number }> = [...prioritySpans];
  if (chosen) removal.push({ index: chosen.index, length: chosen.length });
  let title = stripSpans(text, removal);
  if (chosen) title = stripTrailingTriggers(title);
  title = collapse(title);

  return {
    title,
    priority,
    deadline,
    deadlinePast,
    secondDateIgnored,
    invalidDate: invalid,
    ambiguousDate,
  };
}

// ── Surlignage inline (backdrop façon Todoist) ──────────────────────────────

export type HighlightKind = 'date' | 'priority' | 'mention' | 'attach';

export interface HighlightSegment {
  start: number;
  end: number;
  kind: HighlightKind;
}

// Renvoie les segments [start,end) à colorer dans le texte saisi : la date
// retenue, les tokens priorité, et les `@mention` / `#rattachement` en cours.
// Pur → le composant peint le backdrop à partir de ça. Chevauchements écartés
// (1er par position gagne).
export function computeHighlights(input: string, today: string): HighlightSegment[] {
  const text = input ?? '';
  const raw: HighlightSegment[] = [];

  const { spans } = collectDateSpans(normalizeForMatch(text), parseToday(today));
  spans.sort((a, b) => a.index - b.index);
  const firstDate = spans[0];
  if (firstDate) {
    raw.push({ start: firstDate.index, end: firstDate.index + firstDate.length, kind: 'date' });
  }
  for (const p of collectPrioritySpans(text)) {
    raw.push({ start: p.index, end: p.index + p.length, kind: 'priority' });
  }
  const tokenRe = /[@#][\p{L}\p{N}-]+/gu;
  for (let m = tokenRe.exec(text); m; m = tokenRe.exec(text)) {
    raw.push({
      start: m.index,
      end: m.index + m[0].length,
      kind: m[0].startsWith('@') ? 'mention' : 'attach',
    });
  }

  raw.sort((a, b) => a.start - b.start);
  const out: HighlightSegment[] = [];
  let cursor = 0;
  for (const s of raw) {
    if (s.start < cursor) continue;
    out.push(s);
    cursor = s.end;
  }
  return out;
}

// ── Assemblage du payload de création ───────────────────────────────────────

// Construit le corps POST /actions à partir de l'état de la barre. La
// visibilité de l'action est garantie côté barre : le créateur y est injecté
// comme Réalisateur (R) dès l'ouverture (chip visible + supprimable), donc il
// entre dans le périmètre RACI sans affectation cachée ici. Pure → testée.
export function buildCapturePayload(inp: CapturePayloadInput): Record<string, unknown> {
  const { parse, people, ownerId, attachments, defaultStatusId } = inp;
  const approver = people.find((p) => p.role === 'A');
  const idsForRole = (role: CaptureRole): number[] =>
    people.filter((p) => p.role === role).map((p) => p.id);
  const idsForKind = (kind: CaptureAttachmentInput['kind']): number[] =>
    attachments.filter((a) => a.kind === kind).map((a) => a.id);
  return {
    text: parse.title,
    deadline: parse.deadline,
    deadline_revised: null,
    status_id: defaultStatusId,
    progress: 0,
    priority: parse.priority ?? 2,
    approver_id: approver ? approver.id : null,
    // Owner = un Réalisateur uniquement ; l'Approbateur n'est jamais Owner.
    owner_id:
      ownerId != null && people.some((p) => p.id === ownerId && p.role === 'R') ? ownerId : null,
    performers: idsForRole('R'),
    consulted: idsForRole('C'),
    informed: idsForRole('I'),
    is_confidential: false,
    project_ids: idsForKind('project'),
    committee_ids: idsForKind('committee'),
    structure_ids: idsForKind('structure'),
  };
}
